import axios from 'axios';
import toast from 'react-hot-toast';
import { supabase, withRetry } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import { updateProductSyncStatus, updateProductStatus, checkProductSyncStatus } from './db';

// Récupère la configuration WooCommerce : localStorage prioritaire, puis variables d'environnement
export function getWooApiUrl(): string {
  return localStorage.getItem('woo_api_url') || import.meta.env.VITE_WOO_API_URL || '';
}

export function getWooConsumerKey(): string {
  return localStorage.getItem('woo_consumer_key') || import.meta.env.VITE_WOO_CONSUMER_KEY || '';
}

export function getWooConsumerSecret(): string {
  return localStorage.getItem('woo_consumer_secret') || import.meta.env.VITE_WOO_CONSUMER_SECRET || '';
}

export function hasWooConfig(): boolean {
  return Boolean(getWooApiUrl() && getWooConsumerKey() && getWooConsumerSecret());
}

// Cache des clients WooCommerce pour éviter les scans répétés
let _customerCache: any[] | null = null;
let _customerCacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Drapeau d'annulation d'une synchronisation en masse en cours
let _bulkSyncCancelled = false;

// Demander l'arrêt de la synchronisation en masse en cours (bouton "Annuler")
export function cancelBulkSync(): void {
  _bulkSyncCancelled = true;
}

// Log des commandes WooCommerce (persisté dans Supabase)
export interface WooOrderLog {
  id?: number;
  timestamp: string;
  clientNumber: string;
  productId: number | null;
  wooProductId: number | null;
  orderId: number | null;
  orderKey: string | null;
  paymentUrl: string | null;
  customerEmail: string | null;
  customerName: string | null;
  customerId: number | null;
  status: 'success' | 'error' | 'no_customer';
  errorMessage: string | null;
  orderTotal: string | null;
  orderStatus: string | null; // pending, processing, completed, on-hold, cancelled, refunded, failed
}

const WOO_LOGS_TABLE = 'woo_order_logs';

// Lire les logs depuis Supabase
export async function getOrderLogs(): Promise<WooOrderLog[]> {
  try {
    const { data, error } = await supabase
      .from(WOO_LOGS_TABLE)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error) {
      console.error('Erreur lecture logs Supabase:', error);
      return [];
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      timestamp: row.created_at,
      clientNumber: row.client_number,
      productId: row.product_id,
      wooProductId: row.woo_product_id,
      orderId: row.order_id,
      orderKey: row.order_key,
      paymentUrl: row.payment_url,
      customerEmail: row.customer_email,
      customerName: row.customer_name,
      customerId: row.customer_id,
      status: row.status,
      errorMessage: row.error_message,
      orderTotal: row.order_total,
      orderStatus: row.order_status,
    }));
  } catch (err) {
    console.error('Exception lecture logs:', err);
    return [];
  }
}

// Effacer tous les logs
export async function clearOrderLogs(): Promise<void> {
  try {
    await supabase.from(WOO_LOGS_TABLE).delete().neq('id', 0);
  } catch (err) {
    console.error('Erreur suppression logs:', err);
  }
}

// Ajouter un log dans Supabase
async function addOrderLog(log: WooOrderLog): Promise<void> {
  try {
    const { error } = await supabase.from(WOO_LOGS_TABLE).insert({
      client_number: log.clientNumber,
      product_id: log.productId,
      woo_product_id: log.wooProductId,
      order_id: log.orderId,
      order_key: log.orderKey,
      payment_url: log.paymentUrl,
      customer_email: log.customerEmail,
      customer_name: log.customerName,
      customer_id: log.customerId,
      status: log.status,
      error_message: log.errorMessage,
      order_total: log.orderTotal,
      order_status: log.orderStatus,
    });
    if (error) console.error('Erreur insertion log Supabase:', error);
  } catch (err) {
    console.error('Exception insertion log:', err);
  }
}

// Envoyer un email panier au client via l'endpoint WordPress custom
export async function sendCartEmail(
  email: string,
  name: string,
  cartUrl: string,
  total: number | string,
  clientNumber: string
): Promise<void> {
  if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) {
    throw new Error('Configuration WooCommerce manquante');
  }

  await axios.post(
    `${getWooApiUrl()}/wp-json/lotismode/v1/send-cart-email`,
    {
      email,
      name,
      cart_url: cartUrl,
      total: String(total || ''),
      client_number: clientNumber
    },
    {
      auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
      timeout: 15000
    }
  );
}

// Récupérer les statuts actuels des commandes depuis WooCommerce et mettre à jour Supabase
export async function fetchOrderStatuses(): Promise<void> {
  if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) return;

  try {
    // Lire tous les logs depuis Supabase
    const { data: allLogs } = await supabase
      .from(WOO_LOGS_TABLE)
      .select('id, order_id, order_status, woo_product_id');

    if (!allLogs || allLogs.length === 0) return;

    const logsWithOrder = allLogs.filter((l: any) => l.order_id !== null);
    const logsWithoutOrder = allLogs.filter((l: any) => l.order_id === null && l.woo_product_id !== null);

    // Récupérer les commandes récentes en une seule requête (pour les 2 cas)
    const recentOrdersResponse = await axios.get(
      `${getWooApiUrl()}/wp-json/wc/v3/orders`,
      {
        params: { per_page: 100, orderby: 'date', order: 'desc' },
        auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
        timeout: 15000
      }
    );
    const recentOrders = recentOrdersResponse.data || [];

    let updated = 0;

    // 1) Logs avec order_id → mettre à jour le statut
    for (const log of logsWithOrder) {
      const wooOrder = recentOrders.find((o: any) => Number(o.id) === Number(log.order_id));
      if (!wooOrder) continue;

      if (wooOrder.status === 'cancelled' && log.woo_product_id) {
        // L'ancienne commande a été annulée (par le hook PHP) → chercher la nouvelle commande payée
        const productId = Number(log.woo_product_id);
        const paidOrder = recentOrders.find((o: any) =>
          Number(o.id) !== Number(log.order_id) &&
          (o.status === 'processing' || o.status === 'completed') &&
          (o.line_items || []).some((item: any) => Number(item.product_id) === productId)
        );

        if (paidOrder) {
          console.log(`Commande #${log.order_id} annulée → remplacée par #${paidOrder.id} (${paidOrder.status})`);
          await supabase
            .from(WOO_LOGS_TABLE)
            .update({
              order_id: paidOrder.id,
              order_key: paidOrder.order_key,
              order_status: paidOrder.status,
              order_total: paidOrder.total
            })
            .eq('id', log.id);
          updated++;
          continue;
        }
      }

      // Mise à jour normale du statut
      if (log.order_status !== wooOrder.status) {
        await supabase
          .from(WOO_LOGS_TABLE)
          .update({ order_status: wooOrder.status, order_total: wooOrder.total })
          .eq('id', log.id);
        updated++;
      }
    }

    // 2) Logs sans order_id → chercher une commande contenant le produit
    for (const log of logsWithoutOrder) {
      const productId = Number(log.woo_product_id);
      // Préférer une commande payée, sinon prendre la plus récente
      const paidOrder = recentOrders.find((o: any) =>
        (o.status === 'processing' || o.status === 'completed') &&
        (o.line_items || []).some((item: any) => Number(item.product_id) === productId)
      );
      const anyOrder = paidOrder || recentOrders.find((o: any) =>
        o.status !== 'cancelled' &&
        (o.line_items || []).some((item: any) => Number(item.product_id) === productId)
      );

      if (anyOrder) {
        console.log(`Produit #${productId} → Commande #${anyOrder.id} (${anyOrder.status})`);
        await supabase
          .from(WOO_LOGS_TABLE)
          .update({
            order_id: anyOrder.id,
            order_key: anyOrder.order_key,
            order_status: anyOrder.status,
            order_total: anyOrder.total
          })
          .eq('id', log.id);
        updated++;
      }
    }

    console.log(`Statuts mis à jour: ${updated} commande(s)`);
  } catch (error) {
    console.warn('Erreur lors de la récupération des statuts:', error);
  }
}

// Corriger les produits LOTIS existants sur WooCommerce (virtual + pas de gestion de stock)
export async function fixWooProductStock(): Promise<number> {
  if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) return 0;

  let fixed = 0;
  let page = 1;
  const perPage = 50;

  try {
    while (true) {
      const response = await axios.get(
        `${getWooApiUrl()}/wp-json/wc/v3/products`,
        {
          params: { per_page: perPage, page, search: 'LOTIS', orderby: 'date', order: 'desc' },
          auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
          timeout: 20000
        }
      );
      const products = response.data || [];
      if (products.length === 0) break;

      for (const p of products) {
        // Corriger seulement si nécessaire
        if (p.manage_stock === false && p.stock_status === 'instock') continue;

        try {
          await axios.put(
            `${getWooApiUrl()}/wp-json/wc/v3/products/${p.id}`,
            { virtual: false, manage_stock: false, stock_status: 'instock' },
            {
              auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
              timeout: 15000
            }
          );
          fixed++;
          console.log(`Produit #${p.id} (${p.name}) corrigé: virtual=true, stock=instock`);
        } catch (e) {
          console.warn(`Erreur correction produit #${p.id}:`, e);
        }
      }

      if (products.length < perPage) break;
      page++;
    }
    console.log(`${fixed} produit(s) LOTIS corrigé(s)`);
    return fixed;
  } catch (error) {
    console.error('Erreur correction produits:', error);
    return fixed;
  }
}

// Importer les commandes WooCommerce existantes (produits LOTIS) dans Supabase
// Si une commande existe déjà → met à jour le statut. Sinon → insère une nouvelle ligne.
export async function importWooOrders(): Promise<number> {
  if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) return 0;

  try {
    // Nettoyer les doublons existants (garder seulement la ligne la plus récente par order_id)
    const { data: allLogs } = await supabase
      .from(WOO_LOGS_TABLE)
      .select('id, order_id, created_at')
      .not('order_id', 'is', null)
      .order('created_at', { ascending: false });

    if (allLogs && allLogs.length > 0) {
      const seen = new Set<number>();
      const toDelete: number[] = [];
      for (const log of allLogs) {
        if (seen.has(log.order_id)) {
          toDelete.push(log.id);
        } else {
          seen.add(log.order_id);
        }
      }
      if (toDelete.length > 0) {
        await supabase.from(WOO_LOGS_TABLE).delete().in('id', toDelete);
        console.log(`${toDelete.length} doublon(s) supprimé(s)`);
      }
    }

    // Récupérer les order_id et woo_product_id déjà dans Supabase (pour éviter les doublons)
    const { data: existingLogs } = await supabase
      .from(WOO_LOGS_TABLE)
      .select('id, order_id, order_status, woo_product_id');
    const existingByOrderId = new Map<number, { id: number; order_status: string }>();
    const existingProductIds = new Set<number>();
    for (const log of existingLogs || []) {
      if (log.order_id) existingByOrderId.set(log.order_id, { id: log.id, order_status: log.order_status });
      if (log.woo_product_id) existingProductIds.add(Number(log.woo_product_id));
    }

    // Récupérer les commandes WooCommerce récentes
    let allOrders: any[] = [];
    let page = 1;
    const perPage = 50;

    while (true) {
      const response = await axios.get(
        `${getWooApiUrl()}/wp-json/wc/v3/orders`,
        {
          params: { per_page: perPage, page, orderby: 'date', order: 'desc' },
          auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
          timeout: 20000
        }
      );
      const orders = response.data || [];
      if (orders.length === 0) break;
      allOrders = allOrders.concat(orders);
      if (orders.length < perPage || allOrders.length >= 200) break;
      page++;
    }

    let processed = 0;
    for (const order of allOrders) {
      const items = order.line_items || [];
      const lotisItem = items.find((item: any) => 
        item.name?.includes('LOTIS') || 
        (item.meta_data || []).some((m: any) => m.key === 'customer_number' && m.value?.includes('LOTIS'))
      );

      if (!lotisItem) continue;

      // Ignorer les commandes annulées/remboursées/échouées
      if (['cancelled', 'refunded', 'failed', 'trash'].includes(order.status)) continue;

      const existing = existingByOrderId.get(order.id);

      if (existing) {
        // Commande existe déjà → mettre à jour le statut si différent
        if (existing.order_status !== order.status) {
          await supabase
            .from(WOO_LOGS_TABLE)
            .update({ order_status: order.status, order_total: order.total })
            .eq('id', existing.id);
          processed++;
        }
        continue;
      }

      // Vérifier si le produit est déjà suivi (éviter doublon par produit)
      const wooProductId = lotisItem.product_id || null;
      if (wooProductId && existingProductIds.has(Number(wooProductId))) continue;

      // Nouvelle commande → insérer
      let clientNumber = '';
      const metaCustomer = (lotisItem.meta_data || []).find((m: any) => m.key === 'customer_number');
      if (metaCustomer) {
        clientNumber = metaCustomer.value;
      } else {
        const match = lotisItem.name?.match(/LOTIS(\d+)/);
        clientNumber = match ? `LOTIS${match[1]}` : lotisItem.name || '';
      }

      const customerName = order.billing ? 
        `${order.billing.first_name || ''} ${order.billing.last_name || ''}`.trim() : null;

      const cartUrl = wooProductId ? `${getWooApiUrl()}/cart/?add-to-cart=${wooProductId}` : null;

      const { error } = await supabase.from(WOO_LOGS_TABLE).insert({
        client_number: clientNumber,
        product_id: null,
        woo_product_id: wooProductId,
        order_id: order.id,
        order_key: order.order_key || null,
        payment_url: cartUrl,
        customer_email: order.billing?.email || null,
        customer_name: customerName,
        customer_id: order.customer_id || null,
        status: 'success',
        error_message: null,
        order_total: order.total || null,
        order_status: order.status || 'pending',
        created_at: order.date_created || new Date().toISOString(),
      });

      if (!error) {
        processed++;
        if (wooProductId) existingProductIds.add(Number(wooProductId));
        existingByOrderId.set(order.id, { id: 0, order_status: order.status });
      }
    }

    console.log(`Import terminé: ${processed} commande(s) traitée(s) sur ${allOrders.length} trouvées`);
    return processed;
  } catch (error) {
    console.error('Erreur import commandes WooCommerce:', error);
    throw error;
  }
}

// Charger tous les clients WooCommerce en cache
async function loadCustomerCache(): Promise<any[]> {
  const now = Date.now();
  if (_customerCache && (now - _customerCacheTimestamp) < CACHE_TTL) {
    console.log(`Cache clients WooCommerce utilisé (${_customerCache.length} clients)`);
    return _customerCache;
  }

  console.log('Chargement du cache clients WooCommerce...');
  const allCustomers: any[] = [];
  let page = 1;
  const perPage = 100;

  try {
    while (page <= 20) { // Max 2000 clients
      const response = await axios.get(
        `${getWooApiUrl()}/wp-json/wc/v3/customers`,
        {
          params: { per_page: perPage, page },
          auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
          timeout: 30000
        }
      );

      if (!response.data || response.data.length === 0) break;
      allCustomers.push(...response.data);
      if (response.data.length < perPage) break;
      page++;
    }

    _customerCache = allCustomers;
    _customerCacheTimestamp = now;
    console.log(`Cache clients WooCommerce chargé: ${allCustomers.length} clients`);
    return allCustomers;
  } catch (error) {
    console.error('Erreur chargement cache clients:', error);
    return _customerCache || [];
  }
}

export function invalidateCustomerCache(): void {
  _customerCache = null;
  _customerCacheTimestamp = 0;
}

export async function sendToWooCommerce(product: any) {
  try {
    if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) {
      throw new Error('Configuration WooCommerce manquante');
    }

    // Vérifier si le produit a déjà été synchronisé (source locale)
    const syncStatus = await checkProductSyncStatus(product.id);
    if (syncStatus?.id_woo) {
      console.log(`Le produit ${product.id} a déjà été synchronisé avec WooCommerce (ID: ${syncStatus.id_woo})`);
      return { id: syncStatus.id_woo, alreadySynced: true };
    }

    // Vérification proactive sur WooCommerce directement (couvre le cas où le produit a été
    // créé sur WooCommerce mais la mise à jour de id_woo en base a échoué, ex: coupure réseau
    // avant la fin de la synchro → évite de créer un doublon lors d'une reprise de synchro)
    const customerNumberCheck = `LOTIS${product.client_number}`;
    try {
      const existingOnWoo = await findExistingWooProduct(customerNumberCheck, product.id);
      if (existingOnWoo) {
        console.log(`Produit déjà présent sur WooCommerce (ID: ${existingOnWoo.id}), pas de nouvel envoi`);
        if (product.id && product.id > 0) {
          try {
            await withRetry(async () => {
              await supabase
                .from('lotismode_products')
                .update({ id_woo: existingOnWoo.id, status: 'completed' })
                .eq('id', product.id);
            });
          } catch (updateError) {
            console.error('Erreur lors de la mise à jour du statut:', updateError);
          }
        }
        return { id: existingOnWoo.id, alreadySynced: true };
      }
    } catch (checkError) {
      console.warn('Vérification WooCommerce préalable impossible, poursuite de l\'envoi:', checkError);
    }

    // Convertir les items au format lisible
    let itemsArray = [];
    if (typeof product.items === 'string') {
      try {
        itemsArray = JSON.parse(product.items);
      } catch (e) {
        // Si le parsing échoue, traiter comme une chaîne simple
        itemsArray = [product.items];
      }
    } else if (Array.isArray(product.items)) {
      itemsArray = product.items;
    }

    // Convertir les items au format texte avec sauts de ligne
    const description = itemsArray.join('\n');

    const productData = {
      name: `Product LOTIS${product.client_number}`,
      type: 'simple',
      regular_price: String(product.total_price),
      description: description,
      categories: [{ id: 138 }],
      manage_stock: false,
      stock_status: 'instock',
      meta_data: [
        {
          key: 'customer_number',
          value: `LOTIS${product.client_number}`
        },
        {
          key: 'original_date',
          value: product.created_at || new Date().toISOString()
        },
        {
          key: 'lotismode_product_id',
          value: product.id ? product.id.toString() : '0'
        }
      ],
      status: 'publish'
    };

    const serializedData = JSON.parse(JSON.stringify(productData));

    // Add retry for the WooCommerce API call
    const response = await axios.post(
      `${getWooApiUrl()}/wp-json/wc/v3/products`,
      serializedData,
      {
        auth: {
          username: getWooConsumerKey(),
          password: getWooConsumerSecret()
        },
        timeout: 30000 // 30 second timeout
      }
    );

    // Si nous avons un ID de produit valide, mettre à jour les références dans la base de données
    if (product.id && product.id > 0) {
      try {
        await withRetry(async () => {
          const { error } = await supabase
            .from('lotismode_products')
            .update({ 
              id_woo: response.data.id,
              // Marquer le produit comme complété pour le retirer de la page d'accueil
              status: 'completed' 
            })
            .eq('id', product.id);

          if (error) {
            console.error('Erreur lors de la mise à jour de l\'ID WooCommerce:', error);
            throw new Error('Erreur lors de la mise à jour de l\'ID WooCommerce');
          }
        });
      } catch (updateError) {
        console.error('Erreur lors de la mise à jour du statut:', updateError);
        // Continuer même en cas d'erreur car le produit a déjà été créé sur WooCommerce
      }
    }

    const wooProductId = response.data.id;
    const cartUrl = `${getWooApiUrl()}/cart/?add-to-cart=${wooProductId}`;
    const customerNumber = `LOTIS${product.client_number}`;

    console.log(`Produit créé sur WooCommerce avec succès (ID: ${wooProductId})`);
    console.log(`Lien panier: ${cartUrl}`);

    // Chercher le client WooCommerce pour envoyer l'email
    let customerEmail: string | null = null;
    let customerName: string | null = null;
    let customerId: number | null = null;
    try {
      const customer = await findWooCustomer(customerNumber);
      if (customer) {
        customerEmail = customer.email || customer.billing?.email || null;
        customerName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.username || null;
        customerId = customer.id || null;
      }
    } catch (e) {
      console.warn('Erreur recherche client:', e);
    }

    // Envoyer l'email au client avec le lien add-to-cart
    if (customerEmail) {
      try {
        await sendCartEmail(customerEmail, customerName || '', cartUrl, product.total_price, customerNumber);
        console.log(`Email envoyé à ${customerEmail}`);
        toast.success(`Produit créé ! Email envoyé à ${customerEmail}`, { duration: 5000 });
      } catch (emailError) {
        console.error('Erreur envoi email:', emailError);
        toast.success(`Produit créé ! Email non envoyé - lien: ${cartUrl}`, { duration: 8000 });
      }
    } else {
      toast.success(`Produit créé (ID: ${wooProductId}) - Aucun client WooCommerce associé`, { duration: 5000 });
    }

    // Logger dans Supabase (pas de commande, juste le produit)
    // NE PAS laisser une erreur ici annuler la création : le produit est déjà sur WooCommerce.
    try {
      await addOrderLog({
        timestamp: new Date().toISOString(),
        clientNumber: customerNumber,
        productId: product.id || null,
        wooProductId,
        orderId: null,
        orderKey: null,
        paymentUrl: cartUrl,
        customerEmail,
        customerName,
        customerId,
        status: customerEmail ? 'success' : 'no_customer',
        errorMessage: customerEmail ? null : 'Aucun compte client WooCommerce trouvé',
        orderTotal: product.total_price?.toString() || null,
        orderStatus: null
      });
    } catch (logError) {
      console.error('Erreur addOrderLog (ignorée, produit déjà créé):', logError);
    }

    // Assurer que le produit est marqué comme complété (erreur ignorée : produit déjà créé)
    if (product.id && product.id > 0) {
      try {
        await updateProductStatus(product.id, 'completed');
      } catch (statusError) {
        console.error('Erreur updateProductStatus (ignorée, produit déjà créé):', statusError);
      }
    }

    return { ...response.data, cartUrl };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi vers WooCommerce:', error);
    
    // Vérifier si l'erreur est due à un doublon
    if (error.response?.data?.code === 'product_invalid_sku' || 
        (error.response?.data?.message && error.response?.data?.message.includes('already exists'))) {
      
      // Essayer de récupérer l'ID du produit existant
      try {
        const customerNumber = `LOTIS${product.client_number}`;
        const existingProduct = await findExistingWooProduct(customerNumber, product.id);
        
        if (existingProduct) {
          console.log(`Produit existant trouvé sur WooCommerce (ID: ${existingProduct.id})`);
          
          // Mettre à jour la référence dans notre base de données et marquer comme complété
          if (product.id && product.id > 0) {
            try {
              await withRetry(async () => {
                await supabase
                  .from('lotismode_products')
                  .update({ 
                    id_woo: existingProduct.id,
                    status: 'completed'
                  })
                  .eq('id', product.id);
              });
            } catch (updateError) {
              console.error('Erreur lors de la mise à jour du statut:', updateError);
            }
          }
            
          return { id: existingProduct.id, alreadyExists: true };
        }
      } catch (findError) {
        console.error('Erreur lors de la recherche du produit existant:', findError);
      }
    }
    
    throw error;
  }
}

// Recherche un produit WooCommerce existant.
// - Si `productId` est fourni : on ne considère comme "doublon" QUE le produit issu
//   exactement du même panier (meta lotismode_product_id). Cela évite de recréer le même
//   panier lors d'une reprise, tout en autorisant un client récurrent (ou plusieurs paniers
//   du même client) à obtenir un nouveau produit pour une nouvelle commande.
// - Sinon (fallback) : on retombe sur la correspondance par numéro client.
async function findExistingWooProduct(customerNumber: string, productId?: number | string | null) {
  try {
    const response = await axios.get(
      `${getWooApiUrl()}/wp-json/wc/v3/products`,
      {
        params: {
          search: customerNumber,
          per_page: 20
        },
        auth: {
          username: getWooConsumerKey(),
          password: getWooConsumerSecret()
        },
        timeout: 30000 // 30 second timeout
      }
    );

    const hasProductId = productId !== undefined && productId !== null && String(productId) !== '0';

    const product = response.data.find((p: any) => {
      if (hasProductId) {
        // Doublon uniquement si c'est le MÊME panier (même ligne en base)
        return p.meta_data?.some((meta: any) =>
          meta.key === 'lotismode_product_id' && String(meta.value) === String(productId)
        );
      }
      // Fallback : correspondance par numéro client
      return p.meta_data?.some((meta: any) =>
        meta.key === 'customer_number' && meta.value === customerNumber
      );
    });

    return product || null;
  } catch (error) {
    console.error('Erreur lors de la recherche du produit existant:', error);
    return null;
  }
}

// Chercher un client WooCommerce par numéro client (champ ACF: customer_number)
async function findWooCustomer(clientNumber: string): Promise<any | null> {
  const num = clientNumber.replace('LOTIS', '');
  const lotisNum = clientNumber.startsWith('LOTIS') ? clientNumber : `LOTIS${clientNumber}`;
  
  console.log(`Recherche du client WooCommerce pour: ${lotisNum} (num: ${num})`);

  // Utiliser le cache pour une recherche rapide
  const customers = await loadCustomerCache();
  
  if (customers.length === 0) {
    console.warn('Aucun client dans le cache WooCommerce');
    return null;
  }

  // Chercher dans le cache
  const found = customers.find((c: any) => {
    // Vérifier le champ ACF 'customer_number' dans les meta_data
    const hasAcfMatch = c.meta_data?.some((m: any) => 
      m.key === 'customer_number' && 
      (m.value === lotisNum || m.value === num || String(m.value) === lotisNum || String(m.value) === num)
    );
    if (hasAcfMatch) return true;

    // Vérifier aussi d'autres champs meta possibles
    const hasOtherMeta = c.meta_data?.some((m: any) => 
      (m.key === 'client_number' || m.key === '_customer_number') && 
      (m.value === num || m.value === lotisNum)
    );
    if (hasOtherMeta) return true;

    // Vérifier username, first_name, etc.
    if (c.username === lotisNum || c.username === num) return true;
    if (c.first_name === lotisNum || c.first_name === num) return true;
    if (c.billing?.first_name === lotisNum || c.billing?.first_name === num) return true;

    return false;
  });

  if (found) {
    console.log(`Client trouvé dans le cache (ID: ${found.id}, email: ${found.email})`);
    return found;
  }

  // Si pas trouvé dans le cache, ne forcer un rechargement QUE si le cache est ancien.
  // Sinon (cache fraîchement chargé), le client n'existe tout simplement pas sur WooCommerce :
  // recharger la liste complète (jusqu'à 2000 clients) à chaque client absent ralentit
  // énormément la synchronisation en masse et donne l'impression que l'envoi "bloque".
  const cacheAge = Date.now() - _customerCacheTimestamp;
  if (cacheAge < 60 * 1000) {
    console.warn(`Aucun client WooCommerce trouvé pour ${lotisNum} (cache récent, ${Math.round(cacheAge / 1000)}s → pas de rechargement)`);
    return null;
  }

  // Si pas trouvé dans le cache, forcer un rechargement et réessayer
  console.log('Client non trouvé dans le cache, rechargement...');
  invalidateCustomerCache();
  const freshCustomers = await loadCustomerCache();
  
  const freshFound = freshCustomers.find((c: any) => {
    const hasAcfMatch = c.meta_data?.some((m: any) => 
      m.key === 'customer_number' && 
      (m.value === lotisNum || m.value === num || String(m.value) === lotisNum || String(m.value) === num)
    );
    if (hasAcfMatch) return true;
    if (c.username === lotisNum || c.username === num) return true;
    if (c.first_name === lotisNum || c.first_name === num) return true;
    return false;
  });

  if (freshFound) {
    console.log(`Client trouvé après rechargement (ID: ${freshFound.id}, email: ${freshFound.email})`);
    return freshFound;
  }

  console.warn(`Aucun client WooCommerce trouvé pour ${lotisNum}`);
  return null;
}

// Récupérer les méthodes de paiement activées sur WooCommerce
async function getDefaultPaymentMethod(): Promise<{ id: string, title: string } | null> {
  try {
    const response = await axios.get(
      `${getWooApiUrl()}/wp-json/wc/v3/payment_gateways`,
      {
        auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
        timeout: 15000
      }
    );
    // Trouver la première méthode de paiement activée
    const enabled = response.data?.find((gw: any) => gw.enabled === true);
    if (enabled) {
      console.log(`Méthode de paiement par défaut: ${enabled.id} (${enabled.title})`);
      return { id: enabled.id, title: enabled.title };
    }
    return null;
  } catch (error) {
    console.warn('Impossible de récupérer les méthodes de paiement:', error);
    return null;
  }
}

// Récupérer les méthodes de livraison configurées sur WooCommerce
async function getDefaultShippingMethod(): Promise<{ method_id: string, method_title: string, total: string } | null> {
  try {
    const response = await axios.get(
      `${getWooApiUrl()}/wp-json/wc/v3/shipping/zones`,
      {
        auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
        timeout: 15000
      }
    );
    
    // Parcourir les zones pour trouver une méthode de livraison active
    for (const zone of response.data || []) {
      try {
        const methodsResponse = await axios.get(
          `${getWooApiUrl()}/wp-json/wc/v3/shipping/zones/${zone.id}/methods`,
          {
            auth: { username: getWooConsumerKey(), password: getWooConsumerSecret() },
            timeout: 15000
          }
        );
        
        const enabledMethod = methodsResponse.data?.find((m: any) => m.enabled === true);
        if (enabledMethod) {
          const cost = enabledMethod.settings?.cost?.value || '0';
          console.log(`Méthode de livraison par défaut: ${enabledMethod.method_id} (${enabledMethod.title}) - ${cost}€`);
          return {
            method_id: enabledMethod.method_id,
            method_title: enabledMethod.title,
            total: cost
          };
        }
      } catch (e) {
        // Continuer avec la zone suivante
      }
    }
    return null;
  } catch (error) {
    console.warn('Impossible de récupérer les méthodes de livraison:', error);
    return null;
  }
}

// Créer une commande WooCommerce pour un produit
export async function createWooOrder(product: any, wooProductId: number): Promise<any> {
  try {
    if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) {
      throw new Error('Configuration WooCommerce manquante');
    }

    const customerNumber = `LOTIS${product.client_number}`;
    
    // Chercher le client WooCommerce + méthode de paiement en parallèle
    const [customer, paymentMethod] = await Promise.all([
      findWooCustomer(customerNumber),
      getDefaultPaymentMethod()
    ]);
    
    if (!customer) {
      console.warn(`Client WooCommerce non trouvé pour ${customerNumber}. Commande créée sans client associé.`);
    }

    // Construire les données de la commande
    const orderData: any = {
      status: 'pending',
      currency: 'EUR',
      set_paid: false,
      payment_method: paymentMethod?.id || '',
      payment_method_title: paymentMethod?.title || '',
      line_items: [
        {
          product_id: wooProductId,
          quantity: 1
        }
      ],
      shipping_lines: [], // Le client choisira sa méthode de livraison via le panier
      meta_data: [
        {
          key: 'customer_number',
          value: customerNumber
        },
        {
          key: 'lotismode_product_id',
          value: product.id ? product.id.toString() : '0'
        },
        {
          key: '_order_source',
          value: 'lotismode'
        }
      ]
    };

    // Si un client WooCommerce est trouvé, l'associer à la commande
    if (customer) {
      // Prioriser l'email du compte (customer.email) sur l'email de facturation (billing.email)
      const customerEmail = customer.email || customer.billing?.email || '';
      console.log(`Email utilisé pour la commande: ${customerEmail} (compte: ${customer.email}, facturation: ${customer.billing?.email})`);
      
      orderData.customer_id = customer.id;
      orderData.billing = {
        first_name: customer.billing?.first_name || customer.first_name || '',
        last_name: customer.billing?.last_name || customer.last_name || '',
        email: customerEmail,
        phone: customer.billing?.phone || '',
        address_1: customer.billing?.address_1 || '',
        city: customer.billing?.city || '',
        postcode: customer.billing?.postcode || '',
        country: customer.billing?.country || 'BE'
      };
    }

    // Étape 1: Créer la commande en statut 'pending'
    const response = await axios.post(
      `${getWooApiUrl()}/wp-json/wc/v3/orders`,
      orderData,
      {
        auth: {
          username: getWooConsumerKey(),
          password: getWooConsumerSecret()
        },
        timeout: 30000
      }
    );

    const order = response.data;
    // Lien add-to-cart pour que le client choisisse sa livraison et paye
    const paymentUrl = `${getWooApiUrl()}/cart/?add-to-cart=${wooProductId}`;
    
    console.log(`Commande WooCommerce créée (ID: ${order.id})`);
    console.log(`Lien de paiement: ${paymentUrl}`);

    // L'email "Détails de la commande" est envoyé automatiquement par le hook PHP
    // (lotismode-order-emails.php → woocommerce_rest_insert_shop_order_object)
    
    const sentToEmail = customer ? (customer.email || customer.billing?.email) : null;
    const customerName = customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.username : null;

    // Logger le résultat
    addOrderLog({
      timestamp: new Date().toISOString(),
      clientNumber: customerNumber,
      productId: product.id || null,
      wooProductId,
      orderId: order.id,
      orderKey: order.order_key,
      paymentUrl,
      customerEmail: sentToEmail,
      customerName,
      customerId: customer?.id || null,
      status: customer ? 'success' : 'no_customer',
      errorMessage: customer ? null : 'Aucun compte client WooCommerce trouvé',
      orderTotal: order.total || product.total_price?.toString() || null,
      orderStatus: order.status || 'pending'
    });

    if (customer) {
      console.log(`Email envoyé à: ${sentToEmail}`);
      toast.success(`Commande créée ! Email envoyé à ${sentToEmail}`, { duration: 5000 });
    } else {
      toast.success(`Commande créée (ID: ${order.id}) - Aucun client WooCommerce associé`, { duration: 5000 });
    }

    return {
      orderId: order.id,
      orderKey: order.order_key,
      paymentUrl: paymentUrl,
      customerEmail: sentToEmail,
      status: order.status
    };
  } catch (error: any) {
    console.error('Erreur lors de la création de la commande WooCommerce:', error);
    
    // Logger l'erreur
    addOrderLog({
      timestamp: new Date().toISOString(),
      clientNumber: `LOTIS${product.client_number}`,
      productId: product.id || null,
      wooProductId,
      orderId: null,
      orderKey: null,
      paymentUrl: null,
      customerEmail: null,
      customerName: null,
      customerId: null,
      status: 'error',
      errorMessage: error.response?.data?.message || error.message,
      orderTotal: null,
      orderStatus: null
    });

    toast.error(`Erreur commande: ${error.response?.data?.message || error.message}`);
    throw error;
  }
}

export async function deleteFromWooCommerce(productId: number) {
  try {
    if (!getWooApiUrl() || !getWooConsumerKey() || !getWooConsumerSecret()) {
      throw new Error('Configuration WooCommerce manquante');
    }

    await axios.delete(
      `${getWooApiUrl()}/wp-json/wc/v3/products/${productId}?force=true`,
      {
        auth: {
          username: getWooConsumerKey(),
          password: getWooConsumerSecret()
        },
        timeout: 30000 // 30 second timeout
      }
    );

    toast.success('Produit supprimé de WooCommerce avec succès');
  } catch (error: any) {
    console.error('Erreur lors de la suppression du produit WooCommerce:', error);
    toast.error(error.response?.data?.message || 'Erreur lors de la suppression du produit');
    throw error;
  }
}

export async function restoreToOrders(product: any) {
  try {
    // Utiliser directement la description et le prix actuels de WooCommerce
    // Convertir la description HTML en format texte simple
    const description = product.description.replace(/<[^>]*>/g, '').trim();
    const items = description.split('\n').filter(Boolean);
    const totalPrice = parseFloat(product.regular_price);
    const customerNumber = product.meta_data.find((meta: any) => meta.key === 'customer_number')?.value?.replace('LOTIS', '');

    if (!customerNumber) {
      throw new Error('Numéro de client manquant');
    }

    if (isNaN(totalPrice)) {
      throw new Error('Prix total invalide');
    }

    return await withRetry(async () => {
      const { data: newProduct, error } = await supabase
        .from('lotismode_products')
        .insert({
          client_number: customerNumber,
          items: JSON.stringify(items),
          total_price: totalPrice,
          status: 'pending',
          created_at: new Date().toISOString(),
          id_woo: product.id
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast.success('Produit restauré dans la liste des commandes');
      return newProduct;
    });
  } catch (error: any) {
    console.error('Erreur lors de la restauration du produit:', error);
    toast.error(error.message || 'Erreur lors de la restauration du produit');
    throw error;
  }
}

export async function startBulkSync(products: any[], onProgress?: (progress: any) => void) {
  // Réinitialiser le drapeau d'annulation au début de chaque synchronisation
  _bulkSyncCancelled = false;

  // Générer un ID de session de synchronisation unique
  const syncId = uuidv4();
  const startTime = new Date().toISOString();
  
  // Initialiser les compteurs
  let syncedCount = 0;
  let failedCount = 0;
  const failedDetails: { clientNumber: string; error: string }[] = [];
  
  // Vérifier UNE SEULE FOIS si la table sync_sessions existe (au lieu de le refaire
  // avant chaque mise à jour → beaucoup moins de requêtes = synchro plus rapide)
  let sessionsTableOk = false;
  try {
    const { error: checkError } = await supabase.from('sync_sessions').select('id').limit(1);
    sessionsTableOk = !checkError;
    if (!sessionsTableOk) {
      console.log('Synchronisation sans suivi de session (table sync_sessions non disponible)');
    }
  } catch {
    sessionsTableOk = false;
  }

  // Helper local pour mettre à jour la session (ne fait rien si la table n'existe pas)
  const updateSession = async (fields: any) => {
    if (!sessionsTableOk) return;
    try {
      await withRetry(async () => {
        await supabase.from('sync_sessions').update(fields).eq('id', syncId);
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la session:', error);
    }
  };

  // Enregistrer la session de synchronisation
  if (sessionsTableOk) {
    try {
      await withRetry(async () => {
        await supabase.from('sync_sessions').insert({
          id: syncId,
          started_at: startTime,
          status: 'in_progress',
          total_products: products.length,
          synced_products: 0,
          failed_products: 0
        });
      });
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la session de synchronisation:', error);
    }
  }

  // Marquer tous les produits comme en attente de synchronisation
  for (const product of products) {
    await updateProductSyncStatus(product.id, 'pending', syncId);
  }

  // Précharger une seule fois le cache clients WooCommerce (sinon c'est le premier
  // produit qui paie le coût du téléchargement complet et la barre reste à 0%)
  try {
    await loadCustomerCache();
  } catch (e) {
    console.warn('Préchargement du cache clients impossible:', e);
  }

  // Synchroniser les produits un par un
  let cancelled = false;
  for (let i = 0; i < products.length; i++) {
    // Arrêt demandé par l'utilisateur ("Annuler")
    if (_bulkSyncCancelled) {
      console.log('Synchronisation annulée par l\'utilisateur');
      cancelled = true;
      break;
    }
    try {
      const product = products[i];

      // Informer de la progression
      if (onProgress) {
        onProgress({
          current: i,
          currentProduct: {
            clientNumber: product.client_number,
            items: JSON.parse(product.items),
            totalPrice: product.total_price
          }
        });
      }

      // Envoyer le produit à WooCommerce (sendToWooCommerce gère déjà la détection
      // des doublons locale + WooCommerce, inutile de re-vérifier le statut ici)
      const result = await sendToWooCommerce(product);

      // Mettre à jour le statut de synchronisation
      await updateProductSyncStatus(product.id, 'synced', syncId, result.id);

      // Mettre à jour le statut du produit
      await updateProductStatus(product.id, 'completed', syncId);

      syncedCount++;
      await updateSession({ synced_products: syncedCount, failed_products: failedCount });

    } catch (error: any) {
      console.error(`Erreur lors de la synchronisation du produit ${products[i].id}:`, error);

      // Marquer le produit comme échoué
      await updateProductSyncStatus(products[i].id, 'failed', syncId);

      failedCount++;
      failedDetails.push({
        clientNumber: products[i].client_number,
        error: error?.response?.data?.message || error?.message || 'Erreur inconnue'
      });

      await updateSession({ synced_products: syncedCount, failed_products: failedCount });
    }
  }

  // Finaliser la session de synchronisation
  const endTime = new Date().toISOString();
  await updateSession({
    completed_at: cancelled ? null : endTime,
    // Si annulée, on laisse la session "in_progress" pour permettre une reprise ultérieure
    status: cancelled ? 'in_progress' : (failedCount > 0 ? 'completed_with_errors' : 'completed'),
    synced_products: syncedCount,
    failed_products: failedCount
  });

  return {
    syncId,
    totalProducts: products.length,
    syncedProducts: syncedCount,
    failedProducts: failedCount,
    failedDetails,
    cancelled
  };
}

export async function resumeSyncSession(syncId: string, onProgress?: (progress: any) => void) {
  try {
    // Vérifier si la table sync_sessions existe
    try {
      const { error: checkError } = await supabase.from('sync_sessions').select('id').limit(1);
      
      if (checkError) {
        console.log('La table sync_sessions n\'existe pas encore. Utilisation d\'un mode de synchronisation simplifié.');
        // Returning minimal object to allow process to continue without the table
        return {
          message: 'Synchronisation simplifiée (sans suivi de session)',
          syncId,
          totalProducts: 0,
          syncedProducts: 0,
          failedProducts: 0
        };
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de la table sync_sessions:', error);
      throw error;
    }
    
    // Récupérer les informations de la session
    const { data: session, error: sessionError } = await withRetry(async () => {
      return await supabase
        .from('sync_sessions')
        .select('*')
        .eq('id', syncId)
        .single();
    });
      
    if (sessionError) throw sessionError;
    
    // Récupérer les produits qui n'ont pas été synchronisés
    const { data: pendingProducts, error: productsError } = await withRetry(async () => {
      return await supabase
        .from('lotismode_products')
        .select('*')
        .eq('sync_id', syncId)
        .eq('sync_status', 'pending');
    });
      
    if (productsError) throw productsError;
    
    // Récupérer les produits qui ont échoué
    const { data: failedProducts, error: failedError } = await withRetry(async () => {
      return await supabase
        .from('lotismode_products')
        .select('*')
        .eq('sync_id', syncId)
        .eq('sync_status', 'failed');
    });
      
    if (failedError) throw failedError;
    
    // Combiner les produits à synchroniser
    const productsToSync = [...(pendingProducts || []), ...(failedProducts || [])];
    
    if (productsToSync.length === 0) {
      return {
        message: 'Aucun produit à synchroniser',
        syncId,
        totalProducts: 0,
        syncedProducts: 0,
        failedProducts: 0
      };
    }
    
    // Mettre à jour le statut de la session
    await withRetry(async () => {
      await supabase.from('sync_sessions').update({
        status: 'in_progress',
        updated_at: new Date().toISOString()
      }).eq('id', syncId);
    });
    
    // Initialiser les compteurs
    let syncedCount = session.synced_products || 0;
    let failedCount = 0;
    
    // Synchroniser les produits un par un
    for (let i = 0; i < productsToSync.length; i++) {
      try {
        const product = productsToSync[i];
        
        // Informer de la progression
        if (onProgress) {
          onProgress({
            current: i,
            currentProduct: {
              clientNumber: product.client_number,
              items: JSON.parse(product.items),
              totalPrice: product.total_price
            }
          });
        }
        
        // Envoyer le produit à WooCommerce
        const result = await sendToWooCommerce(product);
        
        // Mettre à jour le statut de synchronisation
        await updateProductSyncStatus(product.id, 'synced', syncId, result.id);
        
        // Mettre à jour le statut du produit
        await updateProductStatus(product.id, 'completed', syncId);
        
        syncedCount++;
        
        // Mettre à jour la session de synchronisation
        await withRetry(async () => {
          await supabase.from('sync_sessions').update({
            synced_products: syncedCount,
            failed_products: failedCount
          }).eq('id', syncId);
        });
        
      } catch (error) {
        console.error(`Erreur lors de la synchronisation du produit ${productsToSync[i].id}:`, error);
        
        // Marquer le produit comme échoué
        await updateProductSyncStatus(productsToSync[i].id, 'failed', syncId);
        
        failedCount++;
        
        // Mettre à jour la session de synchronisation
        await withRetry(async () => {
          await supabase.from('sync_sessions').update({
            synced_products: syncedCount,
            failed_products: failedCount
          }).eq('id', syncId);
        });
      }
    }
    
    // Finaliser la session de synchronisation
    const endTime = new Date().toISOString();
    await withRetry(async () => {
      await supabase.from('sync_sessions').update({
        completed_at: endTime,
        status: failedCount > 0 ? 'completed_with_errors' : 'completed',
        synced_products: syncedCount,
        failed_products: failedCount
      }).eq('id', syncId);
    });
    
    return {
      syncId,
      totalProducts: productsToSync.length,
      syncedProducts: syncedCount,
      failedProducts: failedCount
    };
  } catch (error) {
    console.error('Erreur lors de la reprise de la session de synchronisation:', error);
    throw error;
  }
}

export async function checkForPendingSync() {
  try {
    // Vérifier si la table sync_sessions existe
    try {
      const { error: checkError } = await supabase.from('sync_sessions').select('id').limit(1);
      
      if (checkError) {
        console.log('La table sync_sessions n\'existe pas encore. Aucune synchronisation en attente.');
        return null;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de la table sync_sessions:', error);
      return null;
    }
    
    // Use retry mechanism for database queries
    return await withRetry(async () => {
      // Vérifier s'il y a une session en cours
      const { data: session, error } = await supabase
        .from('sync_sessions')
        .select('*')
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false })
        .limit(1)
        .single();
        
      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Aucune session en cours
        }
        throw error;
      }
      
      // Vérifier s'il y a des produits en attente ou en échec pour cette session
      const { count: pendingCount, error: pendingError } = await supabase
        .from('lotismode_products')
        .select('id', { count: 'exact' })
        .eq('sync_id', session.id)
        .or('sync_status.eq.pending,sync_status.eq.failed');
        
      if (pendingError) throw pendingError;
      
      // Ne retourner les informations que s'il y a réellement des produits à synchroniser
      if (pendingCount && pendingCount > 0) {
        return {
          session,
          pendingCount
        };
      }
      
      // Si aucun produit en attente, mettre à jour le statut de la session
      await supabase
        .from('sync_sessions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', session.id);
      
      return null;
    }, 3, 2000); // Retry up to 3 times with 2-second initial delay
  } catch (error) {
    console.error('Erreur lors de la vérification des synchronisations en attente:', error);
    return null;
  }
}